import { useLayoutEffect, useRef } from "react";

import { useCircleAIShellStore } from "../store/circleAIShellStore";

interface UseShellSidebarOptions {
  collapsed?: boolean;
  enabled?: boolean;
}

/**
 * Declaratively control the left sidebar visibility while a page is mounted.
 * Captures the previous visibility on mount and restores it on unmount, so a
 * page can collapse the sidebar (e.g. fullscreen forms) without leaking that
 * state to sibling routes.
 *
 * Replacement for the deprecated `AppShell.useAppShellSidebar({ mode })`.
 */
export const useShellSidebar = ({
  enabled = true,
  collapsed,
}: UseShellSidebarOptions) => {
  const setLeftSidebarVisible = useCircleAIShellStore(
    s => s.setLeftSidebarVisible,
  );
  const previousVisibleRef = useRef<boolean | null>(null);

  useLayoutEffect(() => {
    if (!enabled || collapsed === undefined) return undefined;
    previousVisibleRef.current ??=
      useCircleAIShellStore.getState().isLeftSidebarVisible;
    setLeftSidebarVisible(!collapsed);
    return () => {
      if (previousVisibleRef.current !== null) {
        setLeftSidebarVisible(previousVisibleRef.current);
        previousVisibleRef.current = null;
      }
    };
  }, [enabled, collapsed, setLeftSidebarVisible]);
};
